'use client'

import { useCookieConsent } from '@/contexts/CookieConsentContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { CookieCategory, COOKIE_CATEGORIES } from '@/lib/types/cookies';
import CookieCategoryComponent from './CookieCategory';
import Link from 'next/link';
import { useState } from 'react';

export default function CookieSettings() {
  const {
    consent,
    updateConsent,
    showSettings,
    closeSettings,
    acceptAllCookies,
    rejectNonEssentialCookies
  } = useCookieConsent();

  const [localConsent, setLocalConsent] = useState(consent);

  const handleCategoryToggle = (category: CookieCategory, enabled: boolean) => {
    setLocalConsent(prev => ({
      ...prev,
      [category]: enabled
    }));
  };

  const handleSavePreferences = () => {
    updateConsent(localConsent);
    closeSettings();
  };

  const handleAcceptAll = () => {
    acceptAllCookies();
    closeSettings();
  };

  const handleRejectNonEssential = () => {
    rejectNonEssentialCookies();
    closeSettings();
  };

  return (
    <Dialog open={showSettings} onOpenChange={closeSettings}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cookie Preferences</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div>
            <p className="text-sm text-muted-foreground">
              Manage your cookie preferences below. You can enable or disable different types of cookies.
              Note that disabling certain cookies may affect your experience on our website.
            </p>
          </div>

          <div className="space-y-4">
            <CookieCategoryComponent
              category={COOKIE_CATEGORIES[CookieCategory.ESSENTIAL]}
              enabled={localConsent.essential}
              onToggle={(enabled) => handleCategoryToggle(CookieCategory.ESSENTIAL, enabled)}
              disabled={true}
            />

            <CookieCategoryComponent
              category={COOKIE_CATEGORIES[CookieCategory.ANALYTICS]}
              enabled={localConsent.analytics}
              onToggle={(enabled) => handleCategoryToggle(CookieCategory.ANALYTICS, enabled)}
            />

            <CookieCategoryComponent
              category={COOKIE_CATEGORIES[CookieCategory.ERROR_MONITORING]}
              enabled={localConsent.errorMonitoring}
              onToggle={(enabled) => handleCategoryToggle(CookieCategory.ERROR_MONITORING, enabled)}
            />

            <CookieCategoryComponent
              category={COOKIE_CATEGORIES[CookieCategory.FUNCTIONAL]}
              enabled={localConsent.functional}
              onToggle={(enabled) => handleCategoryToggle(CookieCategory.FUNCTIONAL, enabled)}
            />
          </div>

          <Separator />

          <div className="space-y-3">
            <h4 className="text-sm font-medium">Your Rights</h4>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>• Right to access your personal data</p>
              <p>• Right to rectification and erasure</p>
              <p>• Right to data portability</p>
              <p>• Right to withdraw consent at any time</p>
            </div>
            <p className="text-xs text-muted-foreground">
              For questions about your data or to exercise your rights, contact us at{' '}
              <a href="mailto:theodor349@gmail.com" className="underline">
                theodor349@gmail.com
              </a>
            </p>
          </div>

          <Separator />

          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={handleSavePreferences} className="flex-1">
              Save Preferences
            </Button>
            <Button onClick={handleAcceptAll} variant="outline" className="flex-1">
              Accept All
            </Button>
            <Button onClick={handleRejectNonEssential} variant="outline" className="flex-1">
              Reject Non-Essential
            </Button>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            For more information, read our{' '}
            <Link href="/cookie-policy" className="underline hover:no-underline">
              Cookie Policy
            </Link>
            {' '}and{' '}
            <Link href="/privacy-policy" className="underline hover:no-underline">
              Privacy Policy
            </Link>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}